import {
  createRootRoute,
  createRoute,
  createRouter,
  Outlet,
  redirect,
} from "@tanstack/react-router";
import { DashboardLayout } from "./components/layout/DashboardLayout";
import { SettingsConfigLayout } from "./components/layout/SettingsConfigLayout";
import { SettingsLayout } from "./components/layout/SettingsLayout";
import { SettingsLogsLayout } from "./components/layout/SettingsLogsLayout";
import { parseOriginSearch } from "./lib/detail-origin";
import {
  redirectFromRoot,
  requireAuth,
  requireReportAccess,
  requireReviews,
  requireViewAll,
} from "./lib/route-guards";
import { RootLayout } from "./routes/__root";
import { AspirationsReportPage } from "./routes/dashboard/aspirations";
import { CapabilityGapsPage } from "./routes/dashboard/capability-gaps";
import { CertificationsPage } from "./routes/dashboard/certifications";
import { CertificationDetailPage } from "./routes/dashboard/certifications/detail";
import { NotStartedCertsPage } from "./routes/dashboard/certifications/not-started";
import { CompetenciesPage } from "./routes/dashboard/competencies";
import { CompetencyDetailPage } from "./routes/dashboard/competencies/detail";
import { CompetencyReportPage } from "./routes/dashboard/competency-report";
import { ConstraintsPage } from "./routes/dashboard/constraints";
import { CycleFeedbackPage } from "./routes/dashboard/cycle-feedback";
import { HelpPage } from "./routes/dashboard/help";
import { MyCertificationsPage } from "./routes/dashboard/my-certifications";
import { MyDashboardPage } from "./routes/dashboard/my-dashboard";
import { OrgChartPage } from "./routes/dashboard/org-chart";
import { OrgHealthPage } from "./routes/dashboard/org-health";
import { OrganizationPage } from "./routes/dashboard/organization";
import { PeopleTargetsPage } from "./routes/dashboard/people-targets";
import { PersonsPage } from "./routes/dashboard/persons";
import { PersonDetailPage } from "./routes/dashboard/persons/detail";
import { PtoReportPage } from "./routes/dashboard/pto";
import { RecognitionReportPage } from "./routes/dashboard/recognition-report";
import { ReviewFormDetailPage } from "./routes/dashboard/review-form-detail";
import { ReviewFormsPage } from "./routes/dashboard/review-forms";
import { ReviewReportPage } from "./routes/dashboard/review-report";
import { ReviewRoundDetailPage } from "./routes/dashboard/review-round-detail";
import { ReviewTaskPage } from "./routes/dashboard/review-task";
import { ReviewsPage } from "./routes/dashboard/reviews";
import { RolesPage } from "./routes/dashboard/roles";
import { parseRoleCompareSearch, RoleComparePage } from "./routes/dashboard/roles/compare";
import { RoleDetailPage } from "./routes/dashboard/roles/detail";
import { SettingsAnnouncementsPage } from "./routes/dashboard/settings/announcements";
import { SettingsAuditLogPage } from "./routes/dashboard/settings/audit-log";
import { SettingsCategoriesPage } from "./routes/dashboard/settings/config/categories";
import { SettingsChangeReasonsPage } from "./routes/dashboard/settings/config/change-reasons";
import { SettingsCompanyTenetsPage } from "./routes/dashboard/settings/config/company-tenets";
import { SettingsConstraintRatingsPage } from "./routes/dashboard/settings/config/constraint-ratings";
import { SettingsConstraintsPage } from "./routes/dashboard/settings/config/constraints";
import { SettingsEmailTestingPage } from "./routes/dashboard/settings/config/email-testing";
import { SettingsEmployeeProfilesPage } from "./routes/dashboard/settings/config/employee-profiles";
import { SettingsEmploymentTypesPage } from "./routes/dashboard/settings/config/employment-types";
import { SettingsFeatureAvailabilityPage } from "./routes/dashboard/settings/config/feature-availability";
import { SettingsLevelsPage } from "./routes/dashboard/settings/config/levels";
import { SettingsManagementCapabilitiesPage } from "./routes/dashboard/settings/config/management-capabilities";
import { SettingsNotificationTemplatesPage } from "./routes/dashboard/settings/config/notification-templates";
import { SettingsReviewConfigPage } from "./routes/dashboard/settings/config/reviews";
import { SettingsRoleClassificationsPage } from "./routes/dashboard/settings/config/role-classifications";
import { SettingsRoleLevelsPage } from "./routes/dashboard/settings/config/role-levels";
import { SettingsWorkLocationsPage } from "./routes/dashboard/settings/config/work-locations";
import { SettingsEmailOutboxPage } from "./routes/dashboard/settings/email-outbox";
import { SettingsErrorLogPage } from "./routes/dashboard/settings/error-log";
import { SettingsNosSyncPage } from "./routes/dashboard/settings/nos-sync";
import { SettingsReleasesPage } from "./routes/dashboard/settings/releases";
import { SettingsThemePage } from "./routes/dashboard/settings/theme";
import { SettingsUsersPage } from "./routes/dashboard/settings/users";
import { SkillsPage } from "./routes/dashboard/skills";
import { SkillDetailPage } from "./routes/dashboard/skills/detail";
import { MyTeamPage, TeamDetailPage } from "./routes/dashboard/team";
import { WhatsNewPage } from "./routes/dashboard/whats-new";
import { LoginPage } from "./routes/login";

const rootRoute = createRootRoute({ component: RootLayout });

const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/",
  beforeLoad: redirectFromRoot,
});

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/login",
  component: LoginPage,
});

function DashboardLayoutRoute() {
  return (
    <DashboardLayout>
      <Outlet />
    </DashboardLayout>
  );
}

const dashboardLayout = createRoute({
  getParentRoute: () => rootRoute,
  path: "/dashboard",
  beforeLoad: requireAuth,
  component: DashboardLayoutRoute,
});

// /dashboard has no page of its own — everyone's home is My Dashboard. The
// redirect keeps every entry point (login, non-admin guard bounces)
// landing on the same page the sidebar's Dashboard link goes to.
const dashboardIndex = createRoute({
  getParentRoute: () => dashboardLayout,
  path: "/",
  beforeLoad: () => {
    throw redirect({ to: "/dashboard/my" });
  },
});

const myDashboardRoute = createRoute({
  getParentRoute: () => dashboardLayout,
  path: "/my",
  component: MyDashboardPage,
});

const myCertificationsRoute = createRoute({
  getParentRoute: () => dashboardLayout,
  path: "/my/certifications",
  component: MyCertificationsPage,
});

const personsRoute = createRoute({
  getParentRoute: () => dashboardLayout,
  path: "/persons",
  component: PersonsPage,
});

const personDetailRoute = createRoute({
  getParentRoute: () => dashboardLayout,
  path: "/persons/$personId",
  validateSearch: parseOriginSearch,
  component: PersonDetailPage,
});

const organizationRoute = createRoute({
  getParentRoute: () => dashboardLayout,
  path: "/organization",
  component: OrganizationPage,
});

const orgChartRoute = createRoute({
  getParentRoute: () => dashboardLayout,
  path: "/organization/chart",
  component: OrgChartPage,
});

const rolesRoute = createRoute({
  getParentRoute: () => dashboardLayout,
  path: "/roles",
  component: RolesPage,
});

// Static segment — TanStack Router ranks it above the "/roles/$roleId"
// param route, so /dashboard/roles/compare never matches role detail.
const roleCompareRoute = createRoute({
  getParentRoute: () => dashboardLayout,
  path: "/roles/compare",
  validateSearch: parseRoleCompareSearch,
  component: RoleComparePage,
});

const roleDetailRoute = createRoute({
  getParentRoute: () => dashboardLayout,
  path: "/roles/$roleId",
  validateSearch: parseOriginSearch,
  component: RoleDetailPage,
});

const skillsRoute = createRoute({
  getParentRoute: () => dashboardLayout,
  path: "/skills",
  component: SkillsPage,
});

const skillDetailRoute = createRoute({
  getParentRoute: () => dashboardLayout,
  path: "/skills/$skillId",
  validateSearch: parseOriginSearch,
  component: SkillDetailPage,
});

const competenciesRoute = createRoute({
  getParentRoute: () => dashboardLayout,
  path: "/competencies",
  component: CompetenciesPage,
});

const competencyDetailRoute = createRoute({
  getParentRoute: () => dashboardLayout,
  path: "/competencies/$competencyId",
  validateSearch: parseOriginSearch,
  component: CompetencyDetailPage,
});

const certificationsRoute = createRoute({
  getParentRoute: () => dashboardLayout,
  path: "/certifications",
  component: CertificationsPage,
});

// Static segment — ranks above "/certifications/$certificationId", so it never
// matches cert detail. Admin-only (drill-down from the Org Health tile).
const notStartedCertsRoute = createRoute({
  getParentRoute: () => dashboardLayout,
  path: "/certifications/not-started",
  beforeLoad: requireViewAll,
  component: NotStartedCertsPage,
});

const certificationDetailRoute = createRoute({
  getParentRoute: () => dashboardLayout,
  path: "/certifications/$certificationId",
  validateSearch: parseOriginSearch,
  component: CertificationDetailPage,
});

// Admin-only (2026-09-03): the constraints directory is a staffing report over
// everyone's ratings, so it sits with Org Health / PTO / Capacity rather than
// with the open capability pages. VIEW tier, so a read-only admin keeps it.
const constraintsRoute = createRoute({
  getParentRoute: () => dashboardLayout,
  path: "/constraints",
  beforeLoad: requireViewAll,
  component: ConstraintsPage,
});

// Hidden until an admin turns it on at Settings → Config → Feature
// availability. The guard is convenience — the server 404s every
// /api/reviews* route when the flag is off, and that is the real boundary.
const reviewsRoute = createRoute({
  getParentRoute: () => dashboardLayout,
  path: "/reviews",
  beforeLoad: requireReviews,
  component: ReviewsPage,
});

// Review forms are HR configuration, so they need the admin view tier ON TOP of
// the feature flag — a manager with Reviews switched on must not reach the
// builder. Static segment first so it never matches the detail route below.
const reviewFormsRoute = createRoute({
  getParentRoute: () => dashboardLayout,
  path: "/reviews/forms",
  beforeLoad: async () => {
    await requireReviews();
    return requireViewAll();
  },
  component: ReviewFormsPage,
});

// One person's review, read as a whole. NOT admin-gated: a manager reads their
// own reports, and the subject reads theirs once the round closes. The server
// decides, and answers 404 to anybody else.
const reviewReportRoute = createRoute({
  getParentRoute: () => dashboardLayout,
  path: "/reviews/reports/$reviewId",
  beforeLoad: requireReviews,
  component: ReviewReportPage,
});

// Filling in one review. NOT admin-gated — this is the surface everybody has,
// and the server admits only the assigned reviewer (plus an edit admin).
const reviewTaskRoute = createRoute({
  getParentRoute: () => dashboardLayout,
  path: "/reviews/tasks/$reviewerId",
  beforeLoad: requireReviews,
  component: ReviewTaskPage,
});

// A round's detail. Admin-only on top of the feature flag, like the forms.
const reviewRoundDetailRoute = createRoute({
  getParentRoute: () => dashboardLayout,
  path: "/reviews/rounds/$cycleId",
  beforeLoad: async () => {
    await requireReviews();
    return requireViewAll();
  },
  component: ReviewRoundDetailPage,
});

const reviewFormDetailRoute = createRoute({
  getParentRoute: () => dashboardLayout,
  path: "/reviews/forms/$templateId",
  beforeLoad: async () => {
    await requireReviews();
    return requireViewAll();
  },
  component: ReviewFormDetailPage,
});

// How a whole role performed against each competency — EPIC-071. HR/admin see
// every role; a pillar head sees the roles in their pillar (EPIC-076 WI-284).
const competencyReportRoute = createRoute({
  getParentRoute: () => dashboardLayout,
  path: "/reviews/competency-report",
  beforeLoad: async () => {
    await requireReviews();
    return requireReportAccess();
  },
  component: CompetencyReportPage,
});

// Endorsements and top criterion scores for one round — EPIC-074. Same
// HR/admin-only tier as the competency report.
const recognitionReportRoute = createRoute({
  getParentRoute: () => dashboardLayout,
  path: "/reviews/recognition-report",
  beforeLoad: async () => {
    await requireReviews();
    return requireViewAll();
  },
  component: RecognitionReportPage,
});

// HR's cycle-wide feedback triage — EPIC-072. Same HR/admin-only tier.
const cycleFeedbackRoute = createRoute({
  getParentRoute: () => dashboardLayout,
  path: "/reviews/cycle-feedback",
  beforeLoad: async () => {
    await requireReviews();
    return requireViewAll();
  },
  component: CycleFeedbackPage,
});

// Capability gaps across a team or a role — EPIC-023, WI-308. Any signed-in
// person may open it: the scope defaults to their own direct reports, and the
// service gates every score it shows per review. Someone with no reports and
// no entitlement sees an empty page, not somebody else's numbers.
const capabilityGapsRoute = createRoute({
  getParentRoute: () => dashboardLayout,
  path: "/capability-gaps",
  component: CapabilityGapsPage,
});

// The matrix used to be its own page. Kept as a redirect so existing links,
// bookmarks and the Help article still land somewhere.
const capabilityMatrixRedirect = createRoute({
  getParentRoute: () => dashboardLayout,
  path: "/capability-matrix",
  beforeLoad: () => {
    throw redirect({ to: "/dashboard/capability-gaps" });
  },
});

// Admin-only company dashboard — org-health tiles + org-wide expiring certs,
// moved off everyone's home so the main dashboard stays identical for all.
const orgHealthRoute = createRoute({
  getParentRoute: () => dashboardLayout,
  path: "/org-health",
  beforeLoad: requireViewAll,
  component: OrgHealthPage,
});

// Aspirations report — NOT admin-only: non-admin managers see their own
// reports (the page scopes server-side and shows an empty state otherwise).
const aspirationsRoute = createRoute({
  getParentRoute: () => dashboardLayout,
  path: "/aspirations",
  component: AspirationsReportPage,
});

// Two routes rather than one optional param: "/team" is the viewer's own team
// and is what the nav links to, while "/team/$personId" is a specific manager's
// and is linked from their profile. Access is enforced by the API, not here.
const ptoRoute = createRoute({
  getParentRoute: () => dashboardLayout,
  path: "/pto",
  // NOT guarded: open to anyone signed in as of 2026-09-22. The page scopes
  // itself server-side — booked leave for everyone, the pending queue and the
  // reasons on it for admins only. Same shape as `aspirationsRoute` above.
  component: PtoReportPage,
});

// Capacity — NOS's per-person monthly capacity and target utilization.
// Admin-only for the same reason as the PTO report: company-wide, and read
// only. The API enforces it too.
//
// The page is called Capacity; the code behind it is still named
// `people-targets` after NOS's own screen (files, hook, query keys, the
// /api/people-targets routes and the gateway endpoint ids). Renaming the live
// gateway endpoints would mean deleting and recreating them, so the split is
// deliberate — see CLAUDE.md.
const peopleTargetsRoute = createRoute({
  getParentRoute: () => dashboardLayout,
  path: "/capacity",
  beforeLoad: requireViewAll,
  component: PeopleTargetsPage,
});

const myTeamRoute = createRoute({
  getParentRoute: () => dashboardLayout,
  path: "/team",
  component: MyTeamPage,
});

const teamDetailRoute = createRoute({
  getParentRoute: () => dashboardLayout,
  path: "/team/$personId",
  component: TeamDetailPage,
});

function SettingsLayoutRoute() {
  return (
    <SettingsLayout>
      <Outlet />
    </SettingsLayout>
  );
}

const settingsLayout = createRoute({
  getParentRoute: () => dashboardLayout,
  path: "/settings",
  beforeLoad: async (ctx) => {
    await requireViewAll(ctx);
    if (ctx.location.pathname === "/dashboard/settings") {
      throw redirect({ to: "/dashboard/settings/users" });
    }
  },
  component: SettingsLayoutRoute,
});

const settingsUsersRoute = createRoute({
  getParentRoute: () => settingsLayout,
  path: "/users",
  component: SettingsUsersPage,
});

const settingsThemeRoute = createRoute({
  getParentRoute: () => settingsLayout,
  path: "/theme",
  component: SettingsThemePage,
});

function SettingsLogsLayoutRoute() {
  return (
    <SettingsLogsLayout>
      <Outlet />
    </SettingsLogsLayout>
  );
}

// Settings → Logs: the four read-mostly records of what the system did, under
// one tab with a sub-tab switcher — the same shape as Config above.
//
// Note the page files stay at their flat `settings/*.tsx` paths rather than
// moving under `settings/logs/` to mirror these URLs: nesting them a level
// deeper makes knip stop seeing their imports, so it reports every one of the
// four tab components as an unused export and fails the pre-commit hook. The
// URL structure is what matters here; don't "tidy" the file paths to match.
const settingsLogsLayout = createRoute({
  getParentRoute: () => settingsLayout,
  path: "/logs",
  beforeLoad: ({ location }) => {
    if (location.pathname === "/dashboard/settings/logs") {
      throw redirect({ to: "/dashboard/settings/logs/audit" });
    }
  },
  component: SettingsLogsLayoutRoute,
});

const settingsAuditLogRoute = createRoute({
  getParentRoute: () => settingsLogsLayout,
  path: "/audit",
  component: SettingsAuditLogPage,
});

function SettingsConfigLayoutRoute() {
  return (
    <SettingsConfigLayout>
      <Outlet />
    </SettingsConfigLayout>
  );
}

// Settings → Config: every admin-configurable reference list under one tab,
// with a sub-tab switcher for quick hops between them.
const settingsConfigLayout = createRoute({
  getParentRoute: () => settingsLayout,
  path: "/config",
  beforeLoad: ({ location }) => {
    if (location.pathname === "/dashboard/settings/config") {
      throw redirect({ to: "/dashboard/settings/config/categories" });
    }
  },
  component: SettingsConfigLayoutRoute,
});

const settingsCategoriesRoute = createRoute({
  getParentRoute: () => settingsConfigLayout,
  path: "/categories",
  component: SettingsCategoriesPage,
});

// Categories and Themes used to be two separate pages — keep old bookmarks
// and links working by redirecting into the combined page.
const legacySkillCategoriesConfigRedirect = createRoute({
  getParentRoute: () => settingsConfigLayout,
  path: "/skill-categories",
  beforeLoad: () => {
    throw redirect({ to: "/dashboard/settings/config/categories" });
  },
});

const legacyCompetencyThemesConfigRedirect = createRoute({
  getParentRoute: () => settingsConfigLayout,
  path: "/competency-themes",
  beforeLoad: () => {
    throw redirect({ to: "/dashboard/settings/config/categories" });
  },
});

const settingsLevelsRoute = createRoute({
  getParentRoute: () => settingsConfigLayout,
  path: "/levels",
  component: SettingsLevelsPage,
});

const settingsRoleLevelsRoute = createRoute({
  getParentRoute: () => settingsConfigLayout,
  path: "/role-levels",
  component: SettingsRoleLevelsPage,
});

const settingsChangeReasonsRoute = createRoute({
  getParentRoute: () => settingsConfigLayout,
  path: "/change-reasons",
  component: SettingsChangeReasonsPage,
});

const settingsCompanyTenetsRoute = createRoute({
  getParentRoute: () => settingsConfigLayout,
  path: "/company-tenets",
  component: SettingsCompanyTenetsPage,
});

const settingsManagementCapabilitiesRoute = createRoute({
  getParentRoute: () => settingsConfigLayout,
  path: "/management-capabilities",
  component: SettingsManagementCapabilitiesPage,
});

const settingsEmploymentTypesRoute = createRoute({
  getParentRoute: () => settingsConfigLayout,
  path: "/employment-types",
  component: SettingsEmploymentTypesPage,
});

const settingsEmployeeProfilesRoute = createRoute({
  getParentRoute: () => settingsConfigLayout,
  path: "/employee-profiles",
  component: SettingsEmployeeProfilesPage,
});

const settingsRoleClassificationsRoute = createRoute({
  getParentRoute: () => settingsConfigLayout,
  path: "/role-classifications",
  component: SettingsRoleClassificationsPage,
});

const settingsWorkLocationsRoute = createRoute({
  getParentRoute: () => settingsConfigLayout,
  path: "/work-locations",
  component: SettingsWorkLocationsPage,
});

// Staffing constraints config (EPIC-043) — the groupings with their NOS-backed
// values, and the familiarity scale. Two pages, not three: the separate
// "Constraint types" page merged into this one when the values moved to NOS and
// there stopped being anything to author under a grouping.
//
// The `/dashboard/constraints` route is a different page again: the constraints
// DIRECTORY, not an authoring surface.
const settingsConstraintsRoute = createRoute({
  getParentRoute: () => settingsConfigLayout,
  path: "/constraints",
  component: SettingsConstraintsPage,
});

const settingsConstraintRatingsRoute = createRoute({
  getParentRoute: () => settingsConfigLayout,
  path: "/constraint-ratings",
  component: SettingsConstraintRatingsPage,
});

// The review reference lists. Guarded by the feature flag as well as the admin
// tier — every /api/review-config route 404s when Reviews is off, so a stale
// bookmark would otherwise land on a page of failed requests.
const settingsReviewConfigRoute = createRoute({
  getParentRoute: () => settingsConfigLayout,
  path: "/reviews",
  beforeLoad: requireReviews,
  component: SettingsReviewConfigPage,
});

const settingsFeatureAvailabilityRoute = createRoute({
  getParentRoute: () => settingsConfigLayout,
  path: "/feature-availability",
  component: SettingsFeatureAvailabilityPage,
});

const settingsEmailTestingRoute = createRoute({
  getParentRoute: () => settingsConfigLayout,
  path: "/email-testing",
  component: SettingsEmailTestingPage,
});

const settingsNotificationTemplatesRoute = createRoute({
  getParentRoute: () => settingsConfigLayout,
  path: "/notification-templates",
  component: SettingsNotificationTemplatesPage,
});

const settingsEmailOutboxRoute = createRoute({
  getParentRoute: () => settingsLogsLayout,
  path: "/email",
  component: SettingsEmailOutboxPage,
});

// Legacy URLs from before the Config tab existed — keep old bookmarks working.
const legacySkillCategoriesRedirect = createRoute({
  getParentRoute: () => settingsLayout,
  path: "/skill-categories",
  beforeLoad: () => {
    throw redirect({ to: "/dashboard/settings/config/categories" });
  },
});

const legacyLevelsRedirect = createRoute({
  getParentRoute: () => settingsLayout,
  path: "/levels",
  beforeLoad: () => {
    throw redirect({ to: "/dashboard/settings/config/levels" });
  },
});

const settingsReleasesRoute = createRoute({
  getParentRoute: () => settingsLayout,
  path: "/releases",
  component: SettingsReleasesPage,
});

const settingsAnnouncementsRoute = createRoute({
  getParentRoute: () => settingsLayout,
  path: "/announcements",
  component: SettingsAnnouncementsPage,
});

const settingsErrorLogRoute = createRoute({
  getParentRoute: () => settingsLogsLayout,
  path: "/errors",
  component: SettingsErrorLogPage,
});

const settingsNosSyncRoute = createRoute({
  getParentRoute: () => settingsLogsLayout,
  path: "/nos-sync",
  component: SettingsNosSyncPage,
});

// Pre-Logs URLs — keep bookmarks and any pasted links working, exactly as the
// /skill-categories and /levels redirects do for the Config regrouping.
const legacyAuditLogRedirect = createRoute({
  getParentRoute: () => settingsLayout,
  path: "/audit-log",
  beforeLoad: () => {
    throw redirect({ to: "/dashboard/settings/logs/audit" });
  },
});

const legacyErrorLogRedirect = createRoute({
  getParentRoute: () => settingsLayout,
  path: "/error-log",
  beforeLoad: () => {
    throw redirect({ to: "/dashboard/settings/logs/errors" });
  },
});

const legacyEmailOutboxRedirect = createRoute({
  getParentRoute: () => settingsLayout,
  path: "/email-outbox",
  beforeLoad: () => {
    throw redirect({ to: "/dashboard/settings/logs/email" });
  },
});

const legacyNosSyncRedirect = createRoute({
  getParentRoute: () => settingsLayout,
  path: "/nos-sync",
  beforeLoad: () => {
    throw redirect({ to: "/dashboard/settings/logs/nos-sync" });
  },
});

const whatsNewRoute = createRoute({
  getParentRoute: () => dashboardLayout,
  path: "/whats-new",
  component: WhatsNewPage,
});

const helpRoute = createRoute({
  getParentRoute: () => dashboardLayout,
  path: "/help",
  component: HelpPage,
});

const routeTree = rootRoute.addChildren([
  indexRoute,
  loginRoute,
  dashboardLayout.addChildren([
    dashboardIndex,
    myDashboardRoute,
    myCertificationsRoute,
    personsRoute,
    personDetailRoute,
    organizationRoute,
    orgChartRoute,
    rolesRoute,
    roleCompareRoute,
    roleDetailRoute,
    skillsRoute,
    skillDetailRoute,
    competenciesRoute,
    competencyDetailRoute,
    certificationsRoute,
    notStartedCertsRoute,
    certificationDetailRoute,
    capabilityGapsRoute,
    capabilityMatrixRedirect,
    orgHealthRoute,
    aspirationsRoute,
    ptoRoute,
    peopleTargetsRoute,
    myTeamRoute,
    teamDetailRoute,
    constraintsRoute,
    reviewsRoute,
    reviewFormsRoute,
    reviewFormDetailRoute,
    reviewRoundDetailRoute,
    reviewTaskRoute,
    reviewReportRoute,
    competencyReportRoute,
    recognitionReportRoute,
    cycleFeedbackRoute,
    whatsNewRoute,
    helpRoute,
    settingsLayout.addChildren([
      settingsUsersRoute,
      settingsAnnouncementsRoute,
      settingsConfigLayout.addChildren([
        settingsCategoriesRoute,
        legacySkillCategoriesConfigRedirect,
        legacyCompetencyThemesConfigRedirect,
        settingsLevelsRoute,
        settingsRoleLevelsRoute,
        settingsEmploymentTypesRoute,
        settingsEmployeeProfilesRoute,
        settingsRoleClassificationsRoute,
        settingsWorkLocationsRoute,
        settingsConstraintsRoute,
        settingsConstraintRatingsRoute,
        settingsReviewConfigRoute,
        settingsFeatureAvailabilityRoute,
        settingsEmailTestingRoute,
        settingsNotificationTemplatesRoute,
        settingsChangeReasonsRoute,
        settingsCompanyTenetsRoute,
        settingsManagementCapabilitiesRoute,
      ]),
      settingsLogsLayout.addChildren([
        settingsAuditLogRoute,
        settingsErrorLogRoute,
        settingsEmailOutboxRoute,
        settingsNosSyncRoute,
      ]),
      legacyAuditLogRedirect,
      legacyErrorLogRedirect,
      legacyEmailOutboxRedirect,
      legacyNosSyncRedirect,
      legacySkillCategoriesRedirect,
      legacyLevelsRedirect,
      settingsReleasesRoute,
      settingsThemeRoute,
    ]),
  ]),
]);

export const router = createRouter({ routeTree });

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
